import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async ({ email, password, phone, countryCode }, { rejectWithValue }) => {
    try {
      let loginData = { password };
      if (email) {
        loginData.email = email;
      } else if (phone && countryCode) {
        loginData.phone = phone;
        loginData.countryCode = countryCode;
      }

      const loginResponse = await axios.post(
        `${API_URL}/users/signin`,
        loginData
      );

      if (loginResponse.data.token) {
        localStorage.setItem("token", loginResponse.data.token);
        const userRole = loginResponse.data.user?.role?.toLowerCase();
        const userType = userRole === 'admin' ? 'admin' : 'user';
        localStorage.setItem("userType", userType);
        
        return loginResponse.data;
      }

      return rejectWithValue("Login failed: Token not found");
    } catch (error) {
      console.error("Login error details:", error);
      
      if (error.response?.data?.message?.includes("verify your email")) {
        return rejectWithValue("Please verify your email");
      }
      
      return rejectWithValue(
        error.response?.data?.message || "Login failed"
      );
    }
  }
);

// Mevcut registerUser thunk'ı
export const registerUser = createAsyncThunk(
  "auth/registerUser",
  async (requestBody, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_URL}/users/signup`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Registration failed";
      return rejectWithValue(errorMessage);
    }
  }
);

export const handleOAuthSuccess = createAsyncThunk(
  "auth/handleOAuthSuccess",
  async ({ token, role }, { rejectWithValue }) => {
    try {
      if (!token) {
        return rejectWithValue("Token not found");
      }

      // Token'ı localStorage'a kaydet
      localStorage.setItem("token", token);
      
      // Role'a göre userType belirle (büyük/küçük harf kontrolü)
      const normalizedRole = role?.toLowerCase();
      const userType = normalizedRole === 'admin' ? 'admin' : 'user';
      localStorage.setItem("userType", userType);

      // Kullanıcı bilgilerini getir
      const response = await axios.get(`${API_URL}/users/current-user`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return {
        user: response.data.user,
        token: token,
        role: role
      };
    } catch (error) {
      localStorage.removeItem("token");
      localStorage.removeItem("userType");
      return rejectWithValue("OAuth login failed");
    }
  }
);

// YENİ: Therapist OAuth Success Handler
export const handleTherapistOAuthSuccess = createAsyncThunk(
  "auth/handleTherapistOAuthSuccess",
  async ({ token, role }, { rejectWithValue }) => {
    try {
      if (!token) {
        return rejectWithValue("Token not found");
      }

      localStorage.setItem("token", token);
      localStorage.setItem("userRole", role);
      localStorage.setItem("userType", "therapist");

      // Terapist bilgilerini getir
      const response = await axios.get(`${API_URL}/therapists/current-therapist`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return {
        user: response.data.therapist,
        token: token,
        role: role
      };
    } catch (error) {
      localStorage.removeItem("token");
      localStorage.removeItem("userRole");
      localStorage.removeItem("userType");
      return rejectWithValue("OAuth login failed");
    }
  }
);

export const getUser = createAsyncThunk(
  "auth/getUser",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return rejectWithValue("Session not found");
      }
      
      const response = await axios.get(`${API_URL}/users/current-user`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Kullanıcı type'ını localStorage'a kaydet
      if (response.data.user?.role) {
        // Backend'den büyük harfli gelebilir (ADMIN), küçük harfe çevir
        const userRole = response.data.user.role.toLowerCase();
        const userType = userRole === 'admin' ? 'admin' : 'user';
        localStorage.setItem("userType", userType);
        
      }
      
      return response.data;
    } catch (error) {
      localStorage.removeItem("token");
      localStorage.removeItem("userType");
      return rejectWithValue(error.response?.data?.message || "User information not found");
    }
  }
);

export const logoutUser = createAsyncThunk("auth/logoutUser", async () => {
  localStorage.removeItem("token");
  localStorage.removeItem("userType");
  return null;
});

const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: null,
    token: localStorage.getItem("token") || null,
    loading: false,
    error: null,
    verificationSent: false,
    isAccountVerified: true,
    oauthLoading: false,
  },
  reducers: {
    setVerificationSent: (state, action) => {
      state.verificationSent = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    setIsAccountVerified: (state, action) => {
      state.isAccountVerified = action.payload;
    },
    setUser: (state, action) => {
      state.token = action.payload.token;
      state.user = action.payload.user;
    },
    setOAuthLoading: (state, action) => {
      state.oauthLoading = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Login cases
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.user = action.payload.user;
        state.token = action.payload.token;
        localStorage.setItem("token", action.payload.token);
        localStorage.setItem("user", JSON.stringify(action.payload.user));
        state.isAccountVerified = true;
        
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Login failed";
        
        if (action.payload === "Please verify your email") {
          state.isAccountVerified = false;
        }
      })

      // Register cases
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.verificationSent = false;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.verificationSent = true;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.verificationSent = false;
      })

      // OAuth success cases
      .addCase(handleOAuthSuccess.pending, (state) => {
        state.oauthLoading = true;
        state.error = null;
      })
      .addCase(handleOAuthSuccess.fulfilled, (state, action) => {
        state.oauthLoading = false;
        state.error = null;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAccountVerified = true;
      })
      .addCase(handleOAuthSuccess.rejected, (state, action) => {
        state.oauthLoading = false;
        state.error = action.payload;
      })

      // Therapist OAuth success cases
      .addCase(handleTherapistOAuthSuccess.pending, (state) => {
        state.oauthLoading = true;
        state.error = null;
      })
      .addCase(handleTherapistOAuthSuccess.fulfilled, (state, action) => {
        state.oauthLoading = false;
        state.error = null;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAccountVerified = true;
      })
      .addCase(handleTherapistOAuthSuccess.rejected, (state, action) => {
        state.oauthLoading = false;
        state.error = action.payload;
      })

      // GetUser cases
      .addCase(getUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(getUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.error = null;
      })
      .addCase(getUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.user = null;
        state.token = null;
      })

      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.error = null;
      });
  },
});

export const { 
  setVerificationSent, 
  clearError, 
  setIsAccountVerified, 
  setUser,
  setOAuthLoading 
} = authSlice.actions;

export default authSlice.reducer;